import { useEffect, useRef } from 'react';
import './urban-network-loader.css';

/**
 * UrbanNetworkLoaderV3 — trama urbana con íconos animados.
 *
 * Ciclo visual (loop infinito):
 *   FADE_IN (×1) → [
 *     HOLD_URBAN   — ciudad estática
 *     ICONS_IN     — íconos aparecen con bounce staggered izq→dcha
 *     HOLD_ICONS   — ciudad + íconos en reposo
 *     ICONS_OUT    — íconos desaparecen con bounce, mismo orden
 *     PAUSE        — ciudad limpia antes de la convergencia
 *     CONVERGE_FWD — ciudad → W (5 s)
 *     HOLD_W       — W estática
 *     CONVERGE_REV — W → ciudad (5 s)
 *   ] → …
 *
 * Íconos: casitas, árboles, cestos de residuos, buses.
 * Bounce in: easeOutBack (overshoot → settle).
 * Bounce out: puff rápido → colapso easeInQuart.
 *
 * Timing (md/xl): loop = 600+700+1200+600+700+5000+800+5000 = 14 600 ms
 * Timing (sm/xs): loop = 300+500+ 700+400+400+2000+600+2000 =  6 900 ms
 *
 * Animación vía requestAnimationFrame + setAttribute directo (sin re-renders).
 * Archivo paralelo — no modifica V1 ni V2.
 */

export interface UrbanNetworkLoaderV3Props {
  size?: 'xs' | 'sm' | 'md' | 'xl';
  className?: string;
}

// ── Timing ────────────────────────────────────────────────────────
const TIMING_FULL = {
  FADE_IN: 700, HOLD_URBAN: 600,  ICONS_IN: 700,  HOLD_ICONS: 1200,
  ICONS_OUT: 600, PAUSE: 700, CONVERGE: 5000, HOLD_W: 800,
};
const TIMING_COMPACT = {
  FADE_IN: 400, HOLD_URBAN: 300,  ICONS_IN: 500,  HOLD_ICONS: 700,
  ICONS_OUT: 400, PAUSE: 400, CONVERGE: 2000, HOLD_W: 600,
};

// ── Posiciones finales (W alámbrica) ─────────────────────────────
const FP: [number, number][] = [
  [472.5,  853.5 ], [382.5,  695    ], [581,    686.5  ], [433.25, 608.5  ], [484,    522    ],
  [371,    672    ], [472.5,  506    ], [363,    485.5  ], [268.75, 485.5  ], [355,    299    ],
  [166.5,  299    ], [496.5,  506    ], [589.5,  672    ], [627,    602    ], [627,    299    ],
  [695.5,  402.5  ], [669.5,  672    ], [764,    506    ],
  [787.256,854    ], [678.847,686.849], [778.91, 613    ], [832.218,774.679],
  [877.181,695.357], [775.766,522.201], [787.256,506.187],
  [888.671,672.336], [939.753,579.002], [904.658,299    ],
  [990.836,485.668], [1093,   299    ],
];

// ── Posiciones iniciales — estructura urbana radial ──────────────
const UP: [number, number][] = [
  [185,  568],  //  0  arteria — extremo izquierdo
  [340,  552],  //  1  arteria — left
  [510,  545],  //  2  arteria — center-left
  [695,  552],  //  3  arteria — center-right
  [865,  545],  //  4  arteria — right
  [1072, 562],  //  5  arteria — extremo derecho
  [572,  372],  //  6  anillo  — top
  [792,  398],  //  7  anillo  — top-right
  [912,  548],  //  8  anillo  — right
  [852,  698],  //  9  anillo  — bottom-right
  [645,  778],  // 10  anillo  — bottom
  [432,  752],  // 11  anillo  — bottom-left
  [322,  615],  // 12  anillo  — left
  [372,  428],  // 13  anillo  — top-left
  [528,  462],  // 14  centro  — upper-left
  [668,  448],  // 15  centro  — upper-right
  [738,  615],  // 16  centro  — right
  [648,  672],  // 17  centro  — lower-right
  [525,  652],  // 18  centro  — lower-left
  [462,  548],  // 19  centro  — left
  [248,  385],  // 20  periferia — outer upper-left
  [188,  490],  // 21  periferia — outer left
  [205,  668],  // 22  periferia — outer lower-left
  [288,  818],  // 23  periferia — outer bottom-left
  [462,  838],  // 24  periferia — outer bottom
  [932,  388],  // 25  periferia — outer upper-right
  [1065, 468],  // 26  periferia — outer right-upper
  [1088, 658],  // 27  periferia — outer right-lower
  [948,  778],  // 28  periferia — outer bottom-right
  [742,  832],  // 29  periferia — outer bottom
];

// ── Aristas urbanas ───────────────────────────────────────────────
const UE: [number, number][] = [
  [0,1], [1,2], [2,3], [3,4], [4,5],
  [6,7], [7,8], [8,9], [9,10], [10,11], [11,12], [12,13], [13,6],
  [1,13], [1,12], [4,8],
  [14,15], [15,16], [16,17], [17,18], [18,19], [19,14],
  [2,14], [3,15], [6,14], [9,17], [11,18], [12,19],
  [13,20], [12,21], [11,23], [10,24], [7,25], [8,26], [9,28], [10,29],
  [20,21], [21,22], [25,26], [26,27], [27,28],
];

// ── Aristas W ─────────────────────────────────────────────────────
const EI: [number, number][] = [
  [0,1],[0,2],[0,3],[1,3],[1,2],[2,4],[4,3],[2,3],
  [5,6],[5,7],[5,8],[6,9],[6,7],[9,10],[9,7],[10,7],[10,8],[7,8],
  [11,12],[12,13],[11,14],[11,15],[11,13],[11,17],
  [13,16],[16,17],[13,17],[13,14],[17,15],[14,15],
  [18,19],[18,20],[18,21],[22,23],[22,21],[22,20],
  [23,19],[23,20],[19,20],[19,21],
  [24,25],[25,26],[24,27],[24,28],[24,26],[24,29],
  [27,29],[27,28],[29,28],[28,26],
];

// ── Radios de nodos ────────────────────────────────────────────────
const OUTER_R = [12,12,12,11,11,12,12,11,11,12,14,12,11,12,14,11,11,12,14,12,11,12,12,11,12,11,11,12,11,14];
const INNER_R = [6,5.5,5.5,5,5,5.5,5.5,5,5,5.5,7,5.5,5,5.5,7,5,5,5.5,7,5.5,5,5.5,5.5,5,5.5,5,5,5.5,5,7];

// ── Íconos urbanos ────────────────────────────────────────────────
//
// 8 íconos ordenados de izquierda a derecha (x del nodo asignado).
// El stagger de entrada y salida sigue ese orden.
//
type IconType = 'house' | 'tree' | 'bin' | 'bus' | 'lamp';

const ICON_DEFS: { type: IconType; nodeIdx: number }[] = [
  { type: 'lamp',  nodeIdx: 21 },  // x≈188 — farola barrio oeste
  { type: 'tree',  nodeIdx: 13 },  // x≈372 — parque anillo top-left
  { type: 'bin',   nodeIdx: 18 },  // x≈525 — recogida centro lower-left
  { type: 'house', nodeIdx: 14 },  // x≈528 — residencial centro
  { type: 'tree',  nodeIdx: 6  },  // x≈572 — parque anillo top
  { type: 'bin',   nodeIdx: 10 },  // x≈645 — recogida anillo bottom
  { type: 'bus',   nodeIdx: 3  },  // x≈695 — tránsito arteria central
  { type: 'bus',   nodeIdx: 9  },  // x≈852 — tránsito anillo bottom-right
  { type: 'tree',  nodeIdx: 25 },  // x≈932 — parque este
];

const N_ICONS = ICON_DEFS.length;
// Offset vertical: el centro del ícono flota 55 unidades sobre el nodo
const ICON_Y_OFFSET = 55;

// ── Easing ────────────────────────────────────────────────────────
function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
// Bounce in: aparece rápido → se achica levemente → vuelve al tamaño normal
//   0 → 1.0  (primer 35 %, easeOutCubic)
//   1.0 → 0.82 (35–55 %, ease)
//   0.82 → 1.0 (55–100 %, ease)
function bounceSettle(t: number): number {
  if (t < 0.35) {
    const u = t / 0.35;
    return 1 - Math.pow(1 - u, 3);                     // easeOutCubic  0→1
  }
  if (t < 0.55) {
    const u = (t - 0.35) / 0.20;
    return 1 - 0.18 * (3 * u * u - 2 * u * u * u);    // smoothstep    1→0.82
  }
  const u = (t - 0.55) / 0.45;
  return 0.82 + 0.18 * (3 * u * u - 2 * u * u * u);   // smoothstep  0.82→1
}
// Bounce out: espejo del bounce in
//   1.0 → 0.82  (se achica, primer 20 %)
//   0.82 → 1.08 (rebota hacia arriba, 20–45 %)
//   1.08 → 0    (sale disparado / colapsa, 45–100 %)
function bounceOut(t: number): number {
  if (t < 0.20) {
    const u = t / 0.20;
    return 1 - 0.18 * (3 * u * u - 2 * u * u * u);      // 1 → 0.82
  }
  if (t < 0.45) {
    const u = (t - 0.20) / 0.25;
    return 0.82 + 0.26 * (3 * u * u - 2 * u * u * u);   // 0.82 → 1.08
  }
  const u = (t - 0.45) / 0.55;
  return Math.max(0, 1.08 * (1 - u * u * u));            // 1.08 → 0
}

// ── Renderizado de íconos (JSX estático, solo transform varía) ────
function IconPaths({ type }: { type: IconType }): JSX.Element {
  switch (type) {
    case 'house': return (
      <>
        {/* cuerpo + techo */}
        <path d="M0,-38 L28,-6 L28,30 L-28,30 L-28,-6 Z" />
        {/* puerta */}
        <path d="M-10,30 L-10,8 L10,8 L10,30" />
      </>
    );
    case 'tree': return (
      <>
        {/* copa */}
        <ellipse cx="0" cy="-12" rx="20" ry="25" />
        {/* tronco */}
        <line x1="0" y1="13" x2="0" y2="30" strokeWidth={6} />
      </>
    );
    case 'bin': return (
      <>
        {/* cuerpo trapezoidal */}
        <path d="M-18,-5 L-14,32 L14,32 L18,-5 Z" />
        {/* tapa */}
        <line x1="-21" y1="-5" x2="21" y2="-5" />
        {/* asa arco */}
        <path d="M-7,-5 L-5,-20 Q0,-27 5,-20 L7,-5" />
      </>
    );
    case 'bus': return (
      <>
        {/* carrocería */}
        <rect x="-36" y="-18" width="72" height="32" rx="5" />
        {/* ruedas */}
        <circle cx="-22" cy="14" r="7" />
        <circle cx="22" cy="14" r="7" />
        {/* divisiones ventanas */}
        <line x1="-8" y1="-18" x2="-8" y2="14" />
        <line x1="8" y1="-18" x2="8" y2="14" />
      </>
    );
    case 'lamp': return (
      <>
        {/* poste vertical */}
        <line x1="0" y1="30" x2="0" y2="-18" />
        {/* cuello curvado hacia la izquierda */}
        <path d="M0,-18 Q0,-40 -18,-40" fill="none" />
        {/* globo / farol */}
        <circle cx="-18" cy="-40" r="8" />
        {/* rayos de luz cortos */}
        <line x1="-18" y1="-32" x2="-18" y2="-27" strokeOpacity={0.65} />
        <line x1="-24" y1="-35" x2="-27" y2="-31" strokeOpacity={0.65} />
        <line x1="-26" y1="-40" x2="-32" y2="-40" strokeOpacity={0.65} />
        <line x1="-24" y1="-45" x2="-27" y2="-49" strokeOpacity={0.65} />
      </>
    );
  }
}

// ── Componente ────────────────────────────────────────────────────
export function UrbanNetworkLoaderV3({
  size = 'md',
  className,
}: UrbanNetworkLoaderV3Props): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const cls = ['unl', `unl--${size}`, className].filter(Boolean).join(' ');
  const hideUE    = size === 'xs';
  const hideIcons = size === 'xs';

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const T = size === 'sm' || size === 'xs' ? TIMING_COMPACT : TIMING_FULL;
    const LOOP_DUR =
      T.HOLD_URBAN + T.ICONS_IN + T.HOLD_ICONS + T.ICONS_OUT +
      T.PAUSE + T.CONVERGE + T.HOLD_W + T.CONVERGE;

    const UE_FADE_END   = 0.55;
    const EI_FADE_START = 0.30;

    const ueLines   = svg.querySelectorAll<SVGLineElement>('.unl__ue');
    const elLines   = svg.querySelectorAll<SVGLineElement>('.unl__el');
    const noCircles = svg.querySelectorAll<SVGCircleElement>('.unl__no');
    const niCircles = svg.querySelectorAll<SVGCircleElement>('.unl__ni');
    const iconGroups = !hideIcons
      ? svg.querySelectorAll<SVGGElement>('.unl__icon')
      : null;

    // Posiciones UE: fijas en UP[]
    for (let j = 0; j < UE.length; j++) {
      const [a, b] = UE[j];
      ueLines[j].setAttribute('x1', String(UP[a][0]));
      ueLines[j].setAttribute('y1', String(UP[a][1]));
      ueLines[j].setAttribute('x2', String(UP[b][0]));
      ueLines[j].setAttribute('y2', String(UP[b][1]));
    }

    // Buffer reutilizable para escalas de íconos (evita alloc en cada frame)
    const iconScales = new Array<number>(N_ICONS).fill(0);

    // Delays de salida independientes — se regeneran cada ciclo al entrar en ICONS_OUT.
    // Cada ícono recibe un float aleatorio en [0, EXIT_MAX_DELAY].
    // La animación de cada ícono dura EXIT_ANIM_DUR (fracción de la fase).
    const EXIT_MAX_DELAY = 0.58;  // el delay más largo ocupa el 58 % de la fase
    const EXIT_ANIM_DUR  = 1 - EXIT_MAX_DELAY; // 42 % restante para el bounceOut
    const exitDelay = new Array<number>(N_ICONS).fill(0);
    let wasInExitPhase = false;

    let startTime: number | null = null;
    let rafId: number;

    function tick(now: number): void {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime;

      let globalOpacity: number;
      let nodeT    = 0;
      let ueOpacity = 0;
      let eiOpacity = 0;
      for (let i = 0; i < N_ICONS; i++) iconScales[i] = 0;

      if (elapsed < T.FADE_IN) {
        // ── FADE_IN (una sola vez) ──────────────────────────────────
        globalOpacity = elapsed / T.FADE_IN;
        nodeT    = 0;
        ueOpacity = hideUE ? 0 : 0.42;
        eiOpacity = 0;

      } else {
        globalOpacity = 1;
        const loop = (elapsed - T.FADE_IN) % LOOP_DUR;

        const L0 = T.HOLD_URBAN;
        const L1 = L0 + T.ICONS_IN;
        const L2 = L1 + T.HOLD_ICONS;
        const L3 = L2 + T.ICONS_OUT;
        const L4 = L3 + T.PAUSE;
        const L5 = L4 + T.CONVERGE;
        const L6 = L5 + T.HOLD_W;

        if (loop < L0) {
          // HOLD_URBAN — ciudad, sin íconos
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;

        } else if (loop < L1) {
          // ICONS_IN — bounce staggered izq→dcha
          // Stagger ocupa el primer 40 % de la fase; cada ícono tarda el 60 % restante
          const ct = (loop - L0) / T.ICONS_IN;
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;
          if (iconGroups) {
            for (let i = 0; i < N_ICONS; i++) {
              const startFrac = (i / (N_ICONS - 1)) * 0.40;
              const localT = Math.max(0, Math.min(1, (ct - startFrac) / 0.60));
              iconScales[i] = bounceSettle(localT);
            }
          }

        } else if (loop < L2) {
          // HOLD_ICONS — ciudad + íconos estáticos
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;
          for (let i = 0; i < N_ICONS; i++) iconScales[i] = 1;

        } else if (loop < L3) {
          // ICONS_OUT — bounce out con orden de salida aleatorio
          if (!wasInExitPhase) {
            // Primera vez que entramos en esta fase: asignar delay aleatorio independiente
            for (let i = 0; i < N_ICONS; i++) {
              exitDelay[i] = Math.random() * EXIT_MAX_DELAY;
            }
            wasInExitPhase = true;
          }
          const ct = (loop - L2) / T.ICONS_OUT;
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;
          if (iconGroups) {
            for (let i = 0; i < N_ICONS; i++) {
              if (ct < exitDelay[i]) {
                iconScales[i] = 1; // aún no ha empezado a salir
              } else {
                const localT = Math.min(1, (ct - exitDelay[i]) / EXIT_ANIM_DUR);
                iconScales[i] = bounceOut(localT);
              }
            }
          }

        } else if (loop < L4) {
          // PAUSE — ciudad limpia; resetear flag para que el próximo ciclo baraje de nuevo
          wasInExitPhase = false;
          nodeT    = 0;
          ueOpacity = hideUE ? 0 : 0.42;
          eiOpacity = 0;

        } else if (loop < L5) {
          // CONVERGE_FWD — ciudad → W
          const ct = (loop - L4) / T.CONVERGE;
          nodeT = easeInOut(ct);
          ueOpacity = hideUE ? 0 : (
            ct < UE_FADE_END ? 0.42 * (1 - ct / UE_FADE_END) : 0
          );
          eiOpacity = ct < EI_FADE_START
            ? 0
            : Math.min(1, (ct - EI_FADE_START) / (1 - EI_FADE_START));

        } else if (loop < L6) {
          // HOLD_W
          nodeT    = 1;
          ueOpacity = 0;
          eiOpacity = 1;

        } else {
          // CONVERGE_REV — W → ciudad (espejo exacto de FWD)
          const ct = (loop - L6) / T.CONVERGE;
          nodeT = easeInOut(1 - ct);
          eiOpacity = ct < (1 - EI_FADE_START)
            ? 1 - ct / (1 - EI_FADE_START)
            : 0;
          ueOpacity = hideUE ? 0 : (
            ct < (1 - UE_FADE_END)
              ? 0
              : 0.42 * (ct - (1 - UE_FADE_END)) / UE_FADE_END
          );
        }
      }

      // Opacidad global del SVG
      svg.style.opacity = String(globalOpacity);

      // Nodos
      for (let i = 0; i < 30; i++) {
        const cx = String(lerp(UP[i][0], FP[i][0], nodeT));
        const cy = String(lerp(UP[i][1], FP[i][1], nodeT));
        noCircles[i].setAttribute('cx', cx);
        noCircles[i].setAttribute('cy', cy);
        niCircles[i].setAttribute('cx', cx);
        niCircles[i].setAttribute('cy', cy);
      }

      // Calles urbanas (opacidad; posición fija)
      const ueStr = String(ueOpacity);
      for (let j = 0; j < ueLines.length; j++) {
        ueLines[j].setAttribute('stroke-opacity', ueStr);
      }

      // Aristas W (siguen a los nodos)
      for (let j = 0; j < EI.length; j++) {
        const [a, b] = EI[j];
        elLines[j].setAttribute('x1', String(lerp(UP[a][0], FP[a][0], nodeT)));
        elLines[j].setAttribute('y1', String(lerp(UP[a][1], FP[a][1], nodeT)));
        elLines[j].setAttribute('x2', String(lerp(UP[b][0], FP[b][0], nodeT)));
        elLines[j].setAttribute('y2', String(lerp(UP[b][1], FP[b][1], nodeT)));
        elLines[j].setAttribute('stroke-opacity', String(eiOpacity));
      }

      // Íconos
      if (iconGroups) {
        for (let i = 0; i < N_ICONS; i++) {
          const s = iconScales[i];
          const [cx, cy] = UP[ICON_DEFS[i].nodeIdx];
          iconGroups[i].setAttribute(
            'transform',
            `translate(${cx},${cy - ICON_Y_OFFSET}) scale(${s})`
          );
        }
      }

      rafId = requestAnimationFrame(tick);
    }

    // Estado inicial
    for (let i = 0; i < 30; i++) {
      noCircles[i].setAttribute('cx', String(UP[i][0]));
      noCircles[i].setAttribute('cy', String(UP[i][1]));
      niCircles[i].setAttribute('cx', String(UP[i][0]));
      niCircles[i].setAttribute('cy', String(UP[i][1]));
    }
    for (let j = 0; j < ueLines.length; j++) {
      ueLines[j].setAttribute('stroke-opacity', hideUE ? '0' : '0.42');
    }
    for (let j = 0; j < elLines.length; j++) {
      elLines[j].setAttribute('stroke-opacity', '0');
    }
    if (iconGroups) {
      for (let i = 0; i < N_ICONS; i++) {
        const [cx, cy] = UP[ICON_DEFS[i].nodeIdx];
        iconGroups[i].setAttribute(
          'transform',
          `translate(${cx},${cy - ICON_Y_OFFSET}) scale(0)`
        );
      }
    }

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      svg.style.opacity = '';
    };
  }, [hideUE, hideIcons, size]);

  return (
    <svg
      ref={svgRef}
      viewBox="110 260 1040 625"
      className={cls}
      focusable="false"
      aria-hidden="true"
      overflow="visible"
    >
      <defs>
        <filter id="unlv3-ue-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.8" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="unlv3-w-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="unlv3-node-glow" x="-400%" y="-400%" width="900%" height="900%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b1" />
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b2" />
          <feMerge>
            <feMergeNode in="b1" />
            <feMergeNode in="b2" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Calles urbanas */}
      <g stroke="#2ab8e8" strokeWidth="0.9" fill="none" strokeLinecap="round" filter="url(#unlv3-ue-glow)">
        {UE.map((_, i) => <line key={i} className="unl__ue" x1="0" y1="0" x2="0" y2="0" />)}
      </g>

      {/* Aristas W */}
      <g stroke="#2ab8e8" strokeWidth="1.4" fill="none" strokeLinecap="round" filter="url(#unlv3-w-glow)">
        {EI.map((_, i) => <line key={i} className="unl__el" x1="0" y1="0" x2="0" y2="0" />)}
      </g>

      {/* Nodos outer glow */}
      <g fill="#18a0d0" opacity={0.55} filter="url(#unlv3-node-glow)">
        {OUTER_R.map((r, i) => <circle key={i} className="unl__no" cx="627" cy="577" r={r} />)}
      </g>

      {/* Nodos inner bright */}
      <g fill="#c8f2ff">
        {INNER_R.map((r, i) => <circle key={i} className="unl__ni" cx="627" cy="577" r={r} />)}
      </g>

      {/* Íconos urbanos — transform animado por JS */}
      {!hideIcons && ICON_DEFS.map((def, i) => (
        <g
          key={i}
          className="unl__icon"
          transform="translate(0,0) scale(0)"
          stroke="#2ab8e8"
          fill="none"
          strokeWidth={2.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#unlv3-ue-glow)"
        >
          <IconPaths type={def.type} />
        </g>
      ))}
    </svg>
  );
}
